import { isNotDefined } from '@sgme/fp';
import { useSgwtWidgets } from '@sgwt/sgwt-widgets-react';
import { useCallback } from 'react';
import { useIntl } from 'react-intl';

import { FormField } from '@/App/tiles/ForwardAccumulator/parts/RightPanel/common/FormField';
import {
  useCheckForwardAccumulatorByTradeCapture,
  useForwardAccumulatorSelector,
} from '@/App/tiles/ForwardAccumulator/ProductProvider.tsx';
import { Collapsible } from '@/App/tiles/shared/Collapsible.tsx';
import { SgNumberInput } from '@/components/sg-design-system/components/input/SgNumberInput';
import { getForwardAccumulatorMarginData } from '@/store/forwardAccumulator/selectors.ts';

const CommercialMargin = () => {
  const { formatMessage, locale } = useIntl();
  const { sgwtWebAnalytics } = useSgwtWidgets();

  const check = useCheckForwardAccumulatorByTradeCapture();
  const { markupCurrency, currencyPair, marginData } = useForwardAccumulatorSelector((accumulator) => ({
    markupCurrency: accumulator.properties.markupCurrency,
    currencyPair: accumulator.properties.currencyPair,
    marginData: getForwardAccumulatorMarginData(accumulator),
  }));
  const [ccy1, ccy2] = currencyPair?.split('/') ?? ['', ''];

  const handleNotionalCurrencyClick = useCallback(() => {
    sgwtWebAnalytics?.trackEvent('Forward Accumulator', 'Margin currency changed', markupCurrency);
    if (isNotDefined(currencyPair)) {
      return;
    }

    return check({
      markupCurrency: markupCurrency === ccy2 ? ccy1 : ccy2,
    });
  }, [sgwtWebAnalytics, markupCurrency, currencyPair, ccy1, ccy2, check]);

  const markup = markupCurrency === ccy1 ? marginData?.markupCcy1 : marginData?.markupCcy2;

  return (
    <Collapsible id="commercialMargin" label={formatMessage({ id: 'accumulator.collapse.commercialMargin.label' })}>
      <FormField formattedMessageId="forwardAccumulator.form.marginAmount">
        <div className="d-flex">
          <SgNumberInput locale={locale} value={markup} data-e2e="marginAmountInput" disabled />
          <button
            tabIndex={-1}
            type="button"
            data-e2e="marginAmount-btn"
            onClick={handleNotionalCurrencyClick}
            className="px-1 btn btn-discreet-primary btn-sm"
          >
            {markupCurrency}
          </button>
        </div>
      </FormField>
    </Collapsible>
  );
};

export default CommercialMargin;
